import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../../../../config/api";
import type { AccountingBackend } from "../../../../config/accounting";
import {
  storeToken,
  storeUser,
  storeCompany,
  storeZohoTokens,
  clearAllAuthData,
} from "../../../../shared/data/storage/authStorage";
import { ZohoAuthCard } from "../components/ZohoAuthCard";
import { TallyConnectorModal } from "../components/TallyConnectorModal";
import { useAuth } from "../hooks/useAuth";
import { sameOriginNextPath } from "../utils/sameOriginNextPath";

export const FromMasterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuthFromStorage } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [needsZohoAuth, setNeedsZohoAuth] = useState<boolean>(false);
  const [connectorGate, setConnectorGate] = useState<string | null>(null);
  /** Set after tenant session is stored; avoids re-init when URL query is stripped via replaceState. */
  const dashboardEntryCompleteRef = useRef(false);

  useEffect(() => {
    const initializeFromMaster = async () => {
      try {
        const token = searchParams.get("token");
        const companyId = searchParams.get("company_id");
        const masterAdminUrl = searchParams.get("master_admin_url");
        // Where master wants this hand-off to land. Read here, with the rest
        // of the query, because the URL is stripped by replaceState below.
        // Untrusted (it comes from another origin), so only a same-origin
        // path survives — anything else falls back to /dashboard as before.
        const nextPath = sameOriginNextPath(searchParams.get("next"));

        if (!token) {
          if (dashboardEntryCompleteRef.current) {
            return;
          }
          throw new Error("Token is required");
        }

        // Clear any existing auth data first
        clearAllAuthData();

        // Store master admin URL for logout redirect
        if (masterAdminUrl) {
          localStorage.setItem("master_admin_url", masterAdminUrl);
          localStorage.setItem("came_from_master_admin", "true");
        }

        // Call dashboard-entry API
        const response = await fetch(
          `${API_BASE_URL}/api/auth/dashboard-entry`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ temp_token: token }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to initialize: ${response.status} - ${errorText}`,
          );
        }

        const result = await response.json();

        if (result.success && result.data) {
          const { user, company, token: tenantToken } = result.data;

          // Validate company_id matches (security check)
          if (companyId && company?.company_id) {
            const companyIdStr = company.company_id.toString();
            if (companyIdStr !== companyId) {
              throw new Error("Company ID mismatch - potential security issue");
            }
          }

          // Store the tenant token as access_token
          storeToken(tenantToken);

          // Store user data with correct role from backend
          const userData = {
            id: user.id.toString(),
            email: user.email,
            userName: user.userName || "",
            role: user.role, // This is the role_name from backend
            isActive: user.is_active,
            dateCreated: new Date().toISOString(),
            dateLastLoggedIn: null,
            zohoRefreshToken: user.zoho_refresh_token || null,
          };
          storeUser(userData);

          // Store Zoho refresh token separately if available
          if (user.zoho_refresh_token) {
            storeZohoTokens(user.zoho_refresh_token);
          }

          // Store company data with all fields from backend
          let accountingBackend: AccountingBackend = "zoho";
          if (company) {
            accountingBackend =
              company.accounting_backend === "tally" ? "tally" : "zoho";
            const companyData = {
              companyId: company.company_id.toString(),
              companyName: company.company_name,
              zohoOrgId: company.zoho_org_id || "",
              appUrl: company.app_url || "",
              addressLine1: company.address_line1 || "",
              addressLine2: company.address_line2 || null,
              companyGstEnabled: company.company_gst_enabled || "0",
              // ZOUR-19: the GST boundary date rides in with the flag, so a
              // master-initiated sign-in resolves document GST modes the same
              // way a direct sign-in does.
              gstEnabledFrom: company.gst_enabled_from ?? null,
              state: company.state || "",
              country: company.country || "",
              pincode: company.pincode || "",
              gstNumber: company.gst_number || "",
              panNumber: company.pan_number || "",
              cinNumber: company.cin_number || "",
              currency: company.currency || "INR",
              msme: company.msme || "0",
              vendorType: company.vendor_type || undefined,
              vendor_type: company.vendor_type || undefined,
              amountCompactUnit: company.amount_compact_unit || undefined,
              amountCompactDecimals: company.amount_compact_decimals ?? undefined,
              amountCompactSuffixJson: company.amount_compact_suffix_json ?? null,
              accountingBackend,
              createdAt: company.created_at || new Date().toISOString(),
            };
            storeCompany(companyData);

            // Store company_id for validation
            localStorage.setItem(
              "current_company_id",
              company.company_id.toString(),
            );
          }

          dashboardEntryCompleteRef.current = true;

          // Clear URL parameters for security
          window.history.replaceState({}, "", "/from-master");

          const needsZoho =
            accountingBackend === "zoho" &&
            result.data.needs_zoho_auth === true;
          const userRole = (user.role ?? "").toString().toLowerCase();
          const isClientView = userRole === "client_view";

          // Zoho tenants only: non–client_view users who need Zoho must
          // complete OAuth here before continuing. Tally never shows this.
          if (needsZoho && !isClientView) {
            setNeedsZohoAuth(true);
            return;
          }

          const connectorReady = Boolean(user.tally_connector_ready_at);
          if (accountingBackend === "tally" && !connectorReady) {
            setConnectorGate(isClientView ? "/client-overview" : (nextPath ?? "/dashboard"));
            refreshAuthFromStorage();
            return;
          }

          // Sync AuthContext with stored token/user so ProtectedRoute sees the user
          refreshAuthFromStorage();

          // Role-based redirect: client_view → client-overview, others → the
          // requested `next` when there is a usable one, else dashboard as
          // always. client_view keeps its own destination either way.
          if (isClientView) {
            navigate("/client-overview", { replace: true });
          } else {
            navigate(nextPath ?? "/dashboard", { replace: true });
          }
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err: any) {
        console.error("Failed to initialize from master admin:", err);
        setError(err.message || "Failed to initialize from master admin");
        clearAllAuthData();
        // Redirect to login after a delay
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFromMaster();
    // Intentionally omit searchParams: stripping ?token via replaceState must not re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot init from initial query string
  }, [navigate, refreshAuthFromStorage]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Initializing Dashboard...
            </h2>
            <p className="text-body font-body text-subtext-color">
              Please wait while we set up your dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-6 p-8 max-w-md">
          <div className="text-red-600 text-6xl">⚠️</div>
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Initialization Failed
            </h2>
            <p className="text-body font-body text-subtext-color">{error}</p>
            <p className="text-caption font-caption text-subtext-color mt-4">
              Redirecting to login page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (connectorGate) {
    return (
      <TallyConnectorModal
        onDone={() => {
          navigate(connectorGate, { replace: true });
        }}
      />
    );
  }

  // User came from master and needs Zoho auth before continuing to dashboard
  if (needsZohoAuth) {
    return (
      <div className="flex h-screen w-full flex-col">
        <div className="flex h-full w-full flex-wrap items-start bg-default-background mobile:flex-col mobile:flex-wrap mobile:gap-0">
          <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center gap-12 self-stretch bg-neutral-800 px-12 py-12 mobile:h-auto mobile:w-full mobile:flex-none">
            <div className="flex w-full max-w-[448px] grow shrink-0 basis-0 flex-col items-start justify-center gap-12 mobile:h-auto mobile:w-full mobile:max-w-[448px] mobile:flex-none">
              <div className="flex flex-col items-start justify-center gap-16 pb-32 mobile:px-0 mobile:py-0">
                <div className="flex flex-col items-start gap-2">
                  <span className="text-heading-2 font-heading-2 text-white">
                    Welcome back
                  </span>
                  <span className="text-heading-3 font-heading-3 text-brand-200">
                    Complete your Zoho authentication to continue.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center gap-6 self-stretch border-l border-solid border-neutral-border px-12 py-12">
            <div className="flex w-full max-w-[448px] flex-col items-start justify-center gap-8">
              <ZohoAuthCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
